import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { apiGet } from "@/lib/api";
import useCachedResource from "@/hooks/useCachedResource";

// Valor por defecto: idéntico en orden y destinos al menú que había
// hardcodeado antes de que fuera editable desde /admin > Configuración. Se
// pinta con esto mientras llega `/settings` (o si el backend no responde),
// así que la web nunca se queda sin menú.
const DEFAULT_NAV_LINKS = [
  { href: "/quienes-somos", label: "Quiénes Somos" },
  { href: "/noticias", label: "Noticias" },
  { href: "/formacion", label: "Formación" },
  { href: "/eventos", label: "Eventos" },
  { href: "/recursos", label: "Recursos" },
  { href: "/podcast", label: "Podcast" },
  { href: "/contacto", label: "Contacto" },
];

// Contenido del menú desplegable de 3 columnas, adaptado del mockup de
// diseño (`submenus()` en Web EDH mezcla.dc.html) a las rutas reales.
//
// Se indexa por `href` a propósito: si el admin renombra una etiqueta desde
// el panel, el desplegable se sigue encontrando porque la ruta no cambió. Si
// el admin añade un enlace nuevo que no está aquí, simplemente no tiene
// desplegable — se pinta como un enlace suelto, sin intentar inventar
// columnas para una página que no conocemos.
const SUBMENUS = {
  "/quienes-somos": [
    { title: "Explorar", items: [
      { label: "La Delegación", href: "/quienes-somos" },
      { label: "Nuestros valores", href: "/quienes-somos" },
    ] },
    { title: "Participar", items: [
      { label: "Ser Misionero Digital", href: "/misionero-digital" },
      { label: "Escríbenos", href: "/contacto" },
    ] },
    { title: "Más", items: [
      { label: "Noticias", href: "/noticias" },
      { label: "Descargar la app", href: "/app" },
    ] },
  ],
  "/noticias": [
    { title: "Explorar", items: [
      { label: "Últimas noticias", href: "/noticias" },
      { label: "Palabra del Día", href: "/palabra-del-dia" },
    ] },
    { title: "Seguir", items: [
      { label: "Podcast", href: "/podcast" },
      { label: "Redes sociales", href: "/#redes" },
    ] },
    { title: "Más", items: [
      { label: "Contacto de prensa", href: "/contacto" },
      { label: "La Delegación", href: "/quienes-somos" },
    ] },
  ],
  "/formacion": [
    { title: "Explorar", items: [
      { label: "Cursos y talleres", href: "/formacion" },
      { label: "Eventos y encuentros", href: "/eventos" },
    ] },
    { title: "Participar", items: [
      { label: "Inscribirme", href: "/contacto" },
      { label: "Ser Misionero Digital", href: "/misionero-digital" },
    ] },
    { title: "Más", items: [
      { label: "Recursos", href: "/recursos" },
      { label: "Podcast", href: "/podcast" },
    ] },
  ],
  "/eventos": [
    { title: "Explorar", items: [
      { label: "Agenda diocesana", href: "/eventos" },
      { label: "Formación", href: "/formacion" },
    ] },
    { title: "Participar", items: [
      { label: "Inscribirme", href: "/contacto" },
      { label: "Proponer un evento", href: "/contacto" },
    ] },
    { title: "Más", items: [
      { label: "Noticias", href: "/noticias" },
      { label: "Recursos", href: "/recursos" },
    ] },
  ],
  "/recursos": [
    { title: "Explorar", items: [
      { label: "Guías y manuales", href: "/recursos" },
      { label: "Material evangelizador", href: "/recursos" },
    ] },
    { title: "Usar", items: [
      { label: "Formación", href: "/formacion" },
      { label: "Descargar la app", href: "/app" },
    ] },
    { title: "Más", items: [
      { label: "Contacto", href: "/contacto" },
      { label: "Podcast", href: "/podcast" },
    ] },
  ],
  "/podcast": [
    { title: "Explorar", items: [
      { label: "Todos los episodios", href: "/podcast" },
      { label: "Palabra del Día", href: "/palabra-del-dia" },
    ] },
    { title: "Escuchar", items: [
      { label: "En la app", href: "/app" },
      { label: "En redes sociales", href: "/#redes" },
    ] },
    { title: "Más", items: [
      { label: "Recursos", href: "/recursos" },
      { label: "Contacto", href: "/contacto" },
    ] },
  ],
  "/contacto": [
    { title: "Escríbenos", items: [
      { label: "Formulario de contacto", href: "/contacto" },
      { label: "Solicitar formación", href: "/formacion" },
    ] },
    { title: "Participar", items: [
      { label: "Ser Misionero Digital", href: "/misionero-digital" },
      { label: "Descargar la app", href: "/app" },
    ] },
    { title: "Más", items: [
      { label: "La Delegación", href: "/quienes-somos" },
      { label: "Noticias", href: "/noticias" },
    ] },
  ],
};

function LogoEDH() {
  // Logotipo real de la delegación, en blanco: el navbar siempre se apoya
  // sobre una superficie oscura.
  return (
    <img
      src="/logos/logo-icono.png"
      alt="Logo Evangelización Digital"
      className="h-10 md:h-12 w-auto object-contain"
      style={{ filter: "brightness(0) invert(1)" }}
    />
  );
}

// Cinta promocional sobre el navbar. Vive dentro del mismo contenedor fijo
// que <nav> (ver más abajo) y por tanto queda fija arriba del todo de forma
// permanente — a diferencia del mockup, donde se desplazaba fuera de la
// pantalla con el resto de la página. Es la consecuencia de mantener el
// navbar como `fixed` en vez de `sticky`.
function CintaPromocional() {
  return (
    <aside
      data-testid="promo-ribbon"
      className="text-center px-4 py-2.5 text-[13px] leading-tight"
      style={{ background: "#0b1f3a", color: "#fff" }}
    >
      El Evangelio de cada día, también en tu móvil.{" "}
      <Link
        to="/app"
        className="font-medium hover:underline underline-offset-2"
        style={{ color: "var(--accent-gold)" }}
      >
        Descarga la app ›
      </Link>
    </aside>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null); // href del enlace cuyo desplegable está abierto
  const location = useLocation();
  const toggleRef = useRef(null);
  const openTimer = useRef(null);
  const closeTimer = useRef(null);

  // Menú editable desde /admin > Configuración. `/settings` es público (solo
  // el PUT exige sesión de admin), así que cualquier visitante puede pedirlo.
  const { data: settings } = useCachedResource("site-settings", () => apiGet("/settings"), []);
  const navLinksRaw = settings?.nav_links;
  const navLinks =
    Array.isArray(navLinksRaw) && navLinksRaw.length
      ? navLinksRaw.filter(l => l && l.label && l.href)
      : DEFAULT_NAV_LINKS;

  useEffect(() => { setOpen(false); setOpenSubmenu(null); }, [location]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // El menú móvil aparecía y desaparecía sin más: no se cerraba con Escape, no
  // devolvía el foco al botón y la página de detrás seguía desplazándose bajo
  // el panel abierto.
  useEffect(() => {
    if (!open) return;

    const previo = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = e => {
      if (e.key === "Escape") {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previo;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  // Cierre del menú desplegable de escritorio con Escape. Es un efecto propio
  // y no una rama del de arriba porque este no bloquea el scroll del body: el
  // desplegable flota sobre la página, no la sustituye.
  useEffect(() => {
    if (!openSubmenu) return;
    const onKeyDown = e => { if (e.key === "Escape") setOpenSubmenu(null); };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [openSubmenu]);

  useEffect(() => () => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
  }, []);

  // Retardo de intención: abrir tarda un poco (para no disparar el panel al
  // pasar el ratón de largo hacia otro sitio) y cerrar tarda algo más (para
  // poder cruzar el hueco entre el enlace y el panel sin que se cierre solo).
  const scheduleOpen = href => {
    if (!SUBMENUS[href]) return; // sin contenido de desplegable: enlace suelto
    clearTimeout(closeTimer.current);
    openTimer.current = setTimeout(() => setOpenSubmenu(href), 120);
  };
  const scheduleClose = () => {
    clearTimeout(openTimer.current);
    closeTimer.current = setTimeout(() => setOpenSubmenu(null), 200);
  };
  const cancelClose = () => clearTimeout(closeTimer.current);

  const columnas = openSubmenu ? SUBMENUS[openSubmenu] || [] : [];

  return (
    <>
      {/* El navbar original era `fixed`, no `sticky`, a propósito: así flota
          transparente sobre el héroe a pantalla completa de cada página en
          vez de empujarlo hacia abajo. La cinta nueva tenía que sumarse a ese
          mismo bloque fijo — si se quedara en flujo normal encima, el navbar
          fijo la taparía sin más. Por eso ambas viven aquí dentro, en un
          único contenedor `fixed`, y la cinta ya no desaparece al hacer
          scroll (a cambio de mantener el efecto de héroe a sangre, que es
          lo que ya funcionaba bien). */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <CintaPromocional />

        <nav
          data-testid="navbar"
          onMouseLeave={scheduleClose}
          onBlur={e => {
            if (!e.currentTarget.contains(e.relatedTarget)) setOpenSubmenu(null);
          }}
          className={`border-b transition-[background-color,border-color,backdrop-filter] duration-base ease-out-apple ${
            scrolled ? "navbar-glass border-white/10" : "navbar-top-scrim border-transparent"
          }`}
        >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* La altura se contrae al hacer scroll: el navbar cede sitio a la
              lectura sin desaparecer, como en las cabeceras de Apple. */}
          <div
            className={`flex items-center justify-between transition-[height] duration-base ease-out-apple ${
              scrolled ? "h-14 md:h-16" : "h-16 md:h-20"
            }`}
          >
            <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3 group focus-ring-light rounded-lg">
              <LogoEDH />
              <div className="hidden sm:block">
                <p className="font-heading font-semibold text-sm leading-none text-white">
                  Evangelización Digital
                </p>
                <p className="text-[11px] font-medium tracking-wide uppercase mt-1 text-white/55">
                  Diócesis de Huelva
                </p>
              </div>
            </Link>

            {/* Navegación de escritorio */}
            <div className="hidden lg:flex items-center gap-7">
              {navLinks.map(link => {
                const active = location.pathname === link.href;
                const tieneSubmenu = Boolean(SUBMENUS[link.href]);
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    data-testid={`nav-link-${link.href.replace("/", "")}`}
                    aria-current={active ? "page" : undefined}
                    aria-expanded={tieneSubmenu ? openSubmenu === link.href : undefined}
                    onMouseEnter={() => scheduleOpen(link.href)}
                    onFocus={() => { cancelClose(); if (SUBMENUS[link.href]) setOpenSubmenu(link.href); }}
                    className={`relative py-1 text-[13px] font-normal transition-colors duration-fast focus-ring-light ${
                      active || openSubmenu === link.href ? "text-white" : "text-white/70 hover:text-white"
                    }`}
                  >
                    {link.label}
                    {/* Subrayado de la sección activa. Antes solo cambiaba el
                        tono del texto, una diferencia que casi no se percibía. */}
                    <span
                      aria-hidden="true"
                      className={`absolute left-0 -bottom-0.5 h-px bg-white transition-[width] duration-base ease-out-apple ${
                        active ? "w-full" : "w-0"
                      }`}
                    />
                  </Link>
                );
              })}
            </div>

            {/* Acciones + interruptor móvil */}
            <div className="flex items-center gap-3">
              <div data-nav-ctas="true" className="hidden md:flex items-center gap-3">
                <Link
                  to="/app"
                  data-testid="navbar-cta-app"
                  style={{ borderRadius: "var(--radius-pill)" }}
                  className="inline-flex items-center gap-2 px-5 py-2 text-[13px] font-medium text-white border border-white/40 hover:bg-white/10 transition-colors duration-fast active:scale-[0.97] focus-ring-light"
                >
                  App
                </Link>
                <Link
                  to="/misionero-digital"
                  data-testid="navbar-cta-missionary"
                  style={{ borderRadius: "var(--radius-pill)" }}
                  className="inline-flex items-center gap-2 px-5 py-2 text-[13px] font-medium bg-white text-black hover:opacity-90 transition-opacity duration-fast active:scale-[0.97] focus-ring-light"
                >
                  Ser Misionero
                </Link>
              </div>
              <button
                ref={toggleRef}
                data-testid="navbar-mobile-toggle"
                className="lg:hidden p-2 rounded-lg text-white hover:bg-white/10 transition-colors duration-fast focus-ring-light"
                onClick={() => setOpen(o => !o)}
                aria-label={open ? "Cerrar menú" : "Abrir menú"}
                aria-expanded={open}
                aria-controls="menu-movil"
              >
                {open ? <X className="w-5 h-5" aria-hidden="true" /> : <Menu className="w-5 h-5" aria-hidden="true" />}
              </button>
            </div>
          </div>
        </div>

        {/* Menú desplegable de 3 columnas. Un único panel compartido por los
            siete enlaces (no uno por enlace): solo cambia qué columnas
            muestra, igual que en apple.com — ver brief-apple.md §4. Se anima
            por altura con `grid-template-rows`, la misma técnica que ya usa
            el panel móvil de aquí abajo, no un `height` a pelo. */}
        <div
          data-testid="navbar-submenu"
          inert={!openSubmenu}
          onMouseEnter={cancelClose}
          className="hidden lg:grid overflow-hidden navbar-glass ring-hairline-dark transition-[grid-template-rows] duration-base ease-out-apple"
          style={{ gridTemplateRows: openSubmenu ? "1fr" : "0fr" }}
        >
          <div className="min-h-0">
            <div className="max-w-5xl mx-auto px-12 py-10 grid grid-cols-3 gap-11">
              {columnas.map(col => (
                <div key={col.title}>
                  <p className="text-xs font-normal text-white/50 mb-4">{col.title}</p>
                  <ul className="space-y-3">
                    {col.items.map(it => (
                      <li key={it.label}>
                        <Link
                          to={it.href}
                          className="text-2xl font-semibold tracking-tight text-white/85 hover:text-white transition-colors duration-fast focus-ring-light rounded"
                        >
                          {it.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Menú móvil. Se mantiene montado y se colapsa con grid-template-rows,
            que sí es animable, en lugar de aparecer y desaparecer de golpe.
            `inert` lo saca del orden de tabulación mientras está cerrado. */}
        <div
          id="menu-movil"
          inert={!open}
          data-testid="navbar-mobile-menu"
          className={`lg:hidden grid overflow-hidden navbar-glass transition-[grid-template-rows,opacity] duration-base ease-out-apple ${
            open ? "grid-rows-[1fr] opacity-100 border-t border-white/10" : "grid-rows-[0fr] opacity-0"
          }`}
        >
          <div className="min-h-0">
            <div className="flex flex-col gap-3 py-4 px-6">
              {navLinks.map((link, i) => {
                const active = location.pathname === link.href;
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    aria-current={active ? "page" : undefined}
                    style={{ transitionDelay: open ? `${i * 30}ms` : "0ms" }}
                    className={`py-2 text-sm font-medium border-b border-white/10 transition-[opacity,transform] duration-base ease-out-apple focus-ring-light ${
                      open ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1"
                    } ${active ? "text-white" : "text-white/75"}`}
                  >
                    {link.label}
                  </Link>
                );
              })}
              <Link
                to="/app"
                style={{ borderRadius: "var(--radius-pill)" }}
                className="mt-2 px-4 py-2.5 text-sm font-medium border border-white/40 text-white text-center active:scale-[0.97] transition-transform duration-fast focus-ring-light"
              >
                Descargar App
              </Link>
              <Link
                to="/misionero-digital"
                style={{ borderRadius: "var(--radius-pill)" }}
                className="px-4 py-2.5 text-sm font-medium bg-white text-black text-center active:scale-[0.97] transition-transform duration-fast focus-ring-light"
              >
                Ser Misionero Digital
              </Link>
            </div>
          </div>
        </div>
        </nav>
      </div>

      {/* Velo tras el desplegable de escritorio: oscurece el resto de la
          página (nunca el propio panel) y sirve de "click fuera" para
          cerrarlo. Es el mismo detalle de profundidad de campo que describe
          brief-apple.md §4. */}
      <div
        aria-hidden="true"
        onClick={() => setOpenSubmenu(null)}
        className="hidden lg:block fixed inset-0 z-40 transition-[opacity,visibility] duration-base ease-out-apple"
        style={{
          background: "rgba(0,0,0,.35)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          opacity: openSubmenu ? 1 : 0,
          visibility: openSubmenu ? "visible" : "hidden",
        }}
      />
    </>
  );
}
